import React, { useState, useEffect, useRef } from "react";
import { APP_PATHS, getPostLoginPath } from "../../utils/appPaths.js";
import { Form, FormGroup, Input, Label } from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useNavigate, useLocation, useParams, Link } from "react-router-dom";
import TailChase from "../../Components/Loaders/TailChase";
import logoWhite from "../../assets/images/logo/logo-white.png";
import { useAuth } from "../../_helper/Auth/AuthProvider";
import AuthService, { PENDING_OTP_KEY } from "../../Services/auth/AuthService";
import { Image } from "../../AbstractElements";
import { toUserMessage } from "../../utils/apiErrorMessage";

const PinModalContent = ({ logoClassMain }) => {
  const { loginOtp, isLoggedIn, clearOtpFlow } = useAuth();
  const { param } = useParams();
  const email = decodeURIComponent(param || "");
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const pinInputRef = useRef(null);
  const notifiedRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isLoggedIn && !notifiedRef.current) {
      notifiedRef.current = true;
      toast.success("OTP sent to your email");
    }
    pinInputRef.current?.focus();
  }, [isLoggedIn]);

  useEffect(() => {
    const pendingEmail = AuthService.getPendingOtpEmail();
    if (pendingEmail && email !== pendingEmail) {
      navigate(APP_PATHS.loginOtp(pendingEmail), {
        replace: true,
        state: location.state,
      });
    }
  }, [email, navigate, location.state]);

  const handleBackToLogin = () => {
    if (typeof clearOtpFlow === "function") {
      clearOtpFlow();
    } else {
      sessionStorage.removeItem(PENDING_OTP_KEY);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pin.trim()) {
      toast.error("Please enter the verification code.");
      return;
    }
    setLoading(true);

    try {
      const { message, success } = await loginOtp(pin, email);
      if (success) {
        toast.success(toUserMessage(message, "Verification successful"));
        navigate(getPostLoginPath(location), { replace: true });
      } else {
        toast.error(
          toUserMessage(message, "Invalid or expired code. Please try again.")
        );
      }
    } catch (err) {
      toast.error(toUserMessage(err, "Error verifying code. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="login-card login-card--mono">
        <div>
          <div>
            <Link
              className={`logo ${logoClassMain ? logoClassMain : ""}`}
              to={APP_PATHS.login}
              onClick={handleBackToLogin}
            >
              <Image
                attrImage={{
                  className: "img-fluid for-light login-logo-white",
                  src: logoWhite,
                  alt: "reddiamond.",
                }}
              />
            </Link>
          </div>
          <div className="login-main">
            <Form className="theme-form" onSubmit={handleSubmit}>
              <FormGroup className="position-relative mb-3">
                <Label className="col-form-label">
                  Enter your 6-digit code
                </Label>
                <p className="mb-2" style={{ fontSize: "14px", color: "#555" }}>
                  Sent to {email || "your email"}
                </p>
                <div className="position-relative">
                  <Input
                    innerRef={pinInputRef}
                    className="form-control"
                    type={showPin ? "text" : "password"}
                    name="pin"
                    value={pin}
                    onChange={(e) =>
                      setPin(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    pattern="\d*"
                    inputMode="numeric"
                    maxLength={6}
                    minLength={6}
                    required
                    autoComplete="one-time-code"
                    placeholder="••••••"
                    style={{
                      fontSize: "1.5rem",
                      letterSpacing: "0.35em",
                      textAlign: "center",
                    }}
                  />
                  <div
                    className="position-absolute end-0 top-50 translate-middle-y me-3"
                    style={{ cursor: "pointer", color: "#111" }}
                    onClick={() => setShowPin(!showPin)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        setShowPin(!showPin);
                      }
                    }}
                    aria-label={showPin ? "Hide code" : "Show code"}
                  >
                    {showPin ? (
                      <FiEye style={{ fontSize: "1.1rem" }} />
                    ) : (
                      <FiEyeOff style={{ fontSize: "1.1rem" }} />
                    )}
                  </div>
                </div>
              </FormGroup>
              <button
                type="submit"
                className={`btn btn-login-mono d-block w-100 ${
                  loading ? "disabled" : ""
                }`}
                disabled={loading}
              >
                {loading ? <TailChase /> : "Verify"}
              </button>
              <p className="text-start mb-0 mt-3">
                <Link
                  to={APP_PATHS.login}
                  className="link"
                  onClick={handleBackToLogin}
                >
                  Back to Sign in
                </Link>
              </p>
            </Form>
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={4500} newestOnTop />
    </>
  );
};

export default PinModalContent;
